import { StepHelpers } from "@shared/components/Stepper";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useBillData,
  useConsumerCreditNote,
  useConsumerLatestBill,
  useMeterdata,
  usePlanData,
  useUpdateConsumerSelection,
} from "@features/bx/singleBillGenaration/hooks";
import ExpandablePanel from "@features/bx/singleBillGenaration/components/billFlow/ExpandablePanel";
import { logEvent } from "@shared/analytics/analytics";
import {
  CalendarIcon,
  ChevronDown,
  ChevronUp,
  MapPin,
  Settings,
  User,
} from "lucide-react";
import { Badge } from "@shared/ui/badge";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { TableColumn } from "@shared/types/table";
import { GenericTable } from "@shared/components/GenericTable";
import { formatTerritoryAddress } from "@shared/utils/address";

interface Step2AccountMetersPlansProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

type SectionConfig = {
  title: string;
  icon: React.ReactNode;
  gridClass: string;
  items: {
    label: string;
    value: React.ReactNode;
  }[];
};

const Step2AccountMetersPlans: React.FC<Step2AccountMetersPlansProps> = ({
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const { id, action = "add" } = useParams();
  const [lateFee, setLateFee] = useState(50);
  const [includeLateFee, setIncludeLateFee] = useState(true);
  const [includeOutstandingFee, setIncludeOutstandingFee] = useState(true);
  const [creditBalance, setCreditBalance] = useState(100);
  const [includeCreditBalance, setIncludeCreditBalance] = useState(true);
  const dataLoadedRef = useRef(false);
  const CURRENCY = useCurrency();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();

  const [expandedServices, setExpandedServices] = useState<{
    [key: string]: boolean;
  }>({});
  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step 2 data:", error);
      }
    },
    [stepHelpers, currentStepIndex],
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step 2 data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Helper function to get step 1 data (consumer selection)
  const getStep1Data = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(0); // Step 1 is at index 0
    } catch (error) {
      console.error("Failed to get step 1 data:", error);
      return null;
    }
  }, [stepHelpers]);

  // Get billDraftId from step 1 data
  const step1Data = getStep1Data();
  const billId = step1Data?.billDraftId || id;

  // const {
  //   data: billData,
  //   isLoading: isBillLoading,
  //   isError: isBillError,
  // } = useBillData(billId);

  const { mutateAsync: updateConsumer, isPending: isSubmitting } =
    useUpdateConsumerSelection(billId);

  const consumerDataId = step1Data?.consumerData?.id;
  const remoteConsumerNumber = step1Data?.consumerData?.consumerNo;
  const invoiceStartDate = step1Data?.formData?.startDate;
  const invoiceEndDate = step1Data?.formData?.endDate;

  const { data: consumerCreditNote } = useConsumerCreditNote(consumerDataId);
  const { data: latestBillData } = useConsumerLatestBill(
    step1Data?.consumerData?.id,
  );

  const meterIds = latestBillData?.meters?.map((m: any) => m) || [];
  const planId = latestBillData?.remotePlanId;

  const {
    data: planData,
    isLoading: isPlanLoading,
    isError: isPlanError,
  } = usePlanData(planId);

  const { data: meterData } = useMeterdata(
    meterIds,
    invoiceStartDate,
    invoiceEndDate,
  );

  const groupedMeters: Record<string, any[]> = useMemo(() => {
    if (!Array.isArray(meterData) || meterData.length === 0) return {};
    const groups: Record<string, any[]> = {};
    meterData.forEach((meter: any) => {
      const productCode = meter?.productCode || meter?.product_code || "Other";
      if (!groups[productCode]) {
        groups[productCode] = [];
      }
      groups[productCode].push(meter);
    });
    return groups;
  }, [meterData]);
  // Load saved data on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          // Restore state from saved data
          setLateFee(savedStepData.lateFee || 50);
          setIncludeLateFee(savedStepData.includeLateFee ?? true);
          setIncludeOutstandingFee(savedStepData.includeOutstandingFee ?? true);
          setCreditBalance(savedStepData.creditBalance || 100);
          setIncludeCreditBalance(savedStepData.includeCreditBalance ?? true);
        }

        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step 2 data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData]);

  // Auto-save when state changes
  useEffect(() => {
    if (dataLoadedRef.current) {
      saveStepData({
        lateFee,
        includeLateFee,
        includeOutstandingFee,
        creditBalance,
        includeCreditBalance,
        billData: step1Data?.response
          ? {
              id: step1Data?.response.id,
              consumerData: step1Data?.response.consumerData,
              invoiceStartDate: step1Data?.formData.startDate,
              invoiceEndDate: step1Data?.response.endDate,
              utilityService: step1Data?.utilityService,
            }
          : null,
        lastUpdated: new Date().toISOString(),
      });
    }
  }, [
    lateFee,
    includeLateFee,
    includeOutstandingFee,
    creditBalance,
    includeCreditBalance,
    step1Data,
    // saveStepData,
  ]);

  const handleContinue = () => {
    updateConsumer(
      {
        remoteUtilityId,
        step: {
          step_working_on: 2,
        },
      },
      {
        onSuccess: () => {
          onNext();
        },
      },
    );
  };

  const handleDraft = () => {
    updateConsumer(
      {
        remoteUtilityId,
        step: {
          step_working_on: 2,
        },
      },
      {
        onSuccess: () => {
          navigate("/bx/single-bill");
        },
      },
    );
  };

  const handlePrevious = () => {
    // Save current state before going back
    saveStepData({
      lateFee,
      includeLateFee,
      includeOutstandingFee,
      creditBalance,
      includeCreditBalance,
      navigatedBack: true,
      navigatedBackAt: new Date().toISOString(),
    });

    if (onPrevious) {
      onPrevious();
    }
  };

  const getAddressField = (address, type) => {
    if (address) {
      const serviceAddress = { [type]: address };
      const newAddress = formatTerritoryAddress(serviceAddress, type);
      return formatTerritoryAddress(serviceAddress, type);
    }
  };
  const SECTIONS: SectionConfig[] = [
    {
      title: "Account Details",
      icon: <User className="h-4 w-4" />,
      gridClass: "grid-cols-1 md:grid-cols-5",
      items: [
        { label: "Account No", value: latestBillData?.consumerNo },
        { label: "Email", value: latestBillData?.email },
        { label: "Phone", value: latestBillData?.contactNumber },
        {
          label: "Category",
          value: latestBillData?.categoryDisplay ? (
            <Badge>{latestBillData.categoryDisplay}</Badge>
          ) : (
            "NA"
          ),
        },
        {
          label: "Sub Category",
          value: latestBillData?.subCategoryDisplay ? (
            <Badge>{latestBillData.subCategoryDisplay}</Badge>
          ) : (
            "NA"
          ),
        },
        { label: "Plan", value: planData?.planName },
      ],
    },
    {
      title: "Address Information",
      icon: <MapPin className="h-4 w-4" />,
      gridClass: "grid-cols-1 md:grid-cols-2",
      items: [
        {
          label: "Service Address",
          value: getAddressField(latestBillData?.serviceAddress, "service"),
        },
        {
          label: "Billing Address",
          value: getAddressField(latestBillData?.billingAddress, "billing"),
        },
      ],
    },
    {
      title: "Last Bill Details",
      icon: <CalendarIcon className="h-4 w-4" />,
      gridClass: "grid-cols-1 md:grid-cols-4",
      items: [
        {
          label: "Last Billed Date",
          value: latestBillData?.billData?.createdDate || "N/A",
        },
        {
          label: "Last Bill Amount",
          value: `${CURRENCY} ${latestBillData?.billData?.totalAmountPayable || "N/A"}`,
        },
        {
          label: "Last Payment Date",
          value: `${latestBillData?.billData?.lastPaymentDate || "N/A"}`,
        },
        {
          label: "Last Payment Amount",
          value: `${CURRENCY} ${latestBillData?.billData?.lastPayAmount || "N/A"}`,
        },
      ],
    },
  ];

  const columns: TableColumn[] = [
    {
      key: "meterNumber",
      header: "Resource ID / Serial Number",
      render: (value, row) => (
        <div>
          {row?.meterNumber || row?.meter_number} /{" "}
          {row?.deviceNo || row?.device_no}
        </div>
      ),
    },
    {
      key: "resourceTypeDisplay",
      header: "Resource Type",
      render: (value, row) => value || "N/A",
    },
    {
      key: "previousReading",
      header: "Last Reading",
      render: (value, row) => value ?? "NA",
    },
    {
      key: "previousReadingDate",
      header: "Last Date",
      render: (value, row) => value ?? "NA",
    },
    {
      key: "currentReadingMeterStatusDisplay",
      header: "VEE Status",
      render: (value, row) => value ?? "NA",
    },
    {
      key: "status",
      header: "Assignment Status",
      render: (value) => <Badge>{value}</Badge>,
    },
  ];

  const formatLabel = (key: string) => {
    return key
      .replace(/[_-]/g, " ") // snake_case or kebab-case to space
      .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase to space
      .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize each word
  };

  const toggleService = (serviceName: string) => {
    setExpandedServices((prev) => ({
      ...prev,
      [serviceName]: !prev[serviceName],
    }));
  };

  const renderContent = (service: string) => {
    const effectiveType =
      // planData?.utilityServiceData?.rate?.[service]?.rate?.basisType?.toUpperCase();
      planData?.taxTypeDisplay?.toUpperCase();
    const rateItems = planData?.utilityServiceData?.rate?.[service]?.rate?.rate;
    switch (effectiveType) {
      case "FIXED":
        const fixedRate = planData?.utilityServiceData?.rate?.rate || 0;
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="card-heading-text font-semibold text-blue-900 mb-1">
                Base Unit Rate
              </h3>
              <div className="text-2xl font-bold text-blue-700">
                {CURRENCY} {fixedRate.toFixed(4)}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[100, 200, 500].map((units, index) => (
                <div
                  key={index}
                  className="bg-white border rounded-lg p-3 text-center"
                >
                  <div className="text-sm font-medium text-gray-600">
                    {units} Units
                  </div>
                  <div className="text-lg font-bold text-blue-600">
                    {CURRENCY} {(units * fixedRate).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "FLAT":
        const flatRate =
          planData?.utilityServiceData?.rate?.[service]?.rate?.rate?.[0]
            ?.rate || 0;
        return (
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-green-900 mb-1">
              Flat Unit Rate
            </h3>
            <div className="text-2xl font-bold text-green-700">
              {CURRENCY} {flatRate}
            </div>
            <p className="text-green-700 mt-2 text-sm">
              This rate applies to all consumption regardless of usage amount.
            </p>
          </div>
        );

      case "SLAB":
        return (
          <div className="space-y-3">
            <div className="overflow-hidden bg-white rounded-lg border">
              <div className="grid grid-cols-2  p-3 text-sm font-medium text-gray-700">
                <div>CONSUMPTION RANGE</div>
                <div>UNIT RATE</div>
              </div>

              {(Array.isArray(rateItems) ? rateItems : []).map(
                (item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-2 p-3 border-t border-gray-100 text-sm"
                  >
                    <div className="text-gray-600">
                      {index ===
                      (Array.isArray(rateItems) ? rateItems.length : 0) - 1
                        ? `${item.startTime || 0}+ units`
                        : `${item.startTime || 0} - ${item.endTime || 0} units`}
                    </div>
                    <div className="font-semibold text-blue-600">
                      {CURRENCY} {item?.rate}
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        );

      case "TIME OF USE":
        return (
          <div className="space-y-3">
            <div className="overflow-hidden bg-white rounded-lg border">
              <div className="grid grid-cols-2  p-3 text-sm font-medium text-gray-700">
                <div>TIME PERIOD</div>
                <div>UNIT RATE</div>
              </div>

              {(Array.isArray(rateItems) ? rateItems : []).map(
                (item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-2 p-3 border-t border-gray-100 text-sm"
                  >
                    <div className="text-gray-600">
                      <div className="font-medium text-gray-900">
                        {item.startTime} - {item.endTime}
                      </div>
                    </div>
                    <div className="font-semibold text-indigo-600">
                      {CURRENCY} {item?.rate}
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        );

      case "SEASONAL":
        return (
          <div className="space-y-3">
            <div className="overflow-hidden bg-white rounded-lg border">
              <div className="grid grid-cols-3  p-3 text-sm font-medium text-gray-700">
                <div>SEASON</div>
                <div>PERIOD</div>
                <div>UNIT RATE</div>
              </div>

              {(Array.isArray(rateItems) ? rateItems : []).map(
                (item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-3 p-3 border-t border-gray-100 text-sm"
                  >
                    <div className="font-medium text-gray-900">
                      {item.seasonName || `Season ${index + 1}`}
                    </div>
                    <div className="text-gray-600">
                      {item.startTime} - {item.endTime}
                    </div>
                    <div className="font-semibold text-orange-600">
                      {CURRENCY} {item.rate}
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="text-gray-500 p-4 bg-yellow-50 rounded-lg">
            <p>Unknown rate type: {effectiveType}</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="card-subheading-text">
          Review account details, meters, and billing configuration before
          proceeding.
        </p>
      </div>

      <ExpandablePanel
        title="Account Information"
        statusBadge={latestBillData?.statusDisplay}
      >
        <div className="space-y-8">
          {SECTIONS.map((section) => (
            <div key={section.title} className="space-y-4">
              {/* Section Header */}
              <div className="flex items-center gap-2 text-blue-600 border-b border-blue-100 pb-2">
                {section.icon}
                <h3 className="card-heading-text">{section.title}</h3>
              </div>

              {/* Section Content */}
              <div className={`grid ${section.gridClass} gap-y-6 gap-x-8`}>
                {section.items.map((item) => (
                  <ValueLabel
                    key={item.label}
                    label={item.label}
                    value={item.value}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </ExpandablePanel>

      {/* Meter Information Panel */}
      <ExpandablePanel title="Meter Information">
        <div className="space-y-6">
          {Object.keys(groupedMeters).length > 0 ? (
            Object.entries(groupedMeters).map(([productCode, meters]) => (
              <div key={productCode}>
                {/* Product Code Header */}
                <div className="text-sm font-semibold text-gray-700 mb-2">
                  {productCode} Meters
                </div>

                {/* Table for this product code */}
                <div className="rounded-md border overflow-hidden">
                  <GenericTable
                    data={meters}
                    columns={columns}
                    rowKey="meterNumber"
                    emptyMessage="No meters found"
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-md border overflow-hidden">
              <GenericTable
                data={[]}
                columns={columns}
                rowKey="meterNumber"
                emptyMessage="No meters found"
              />
            </div>
          )}
        </div>
      </ExpandablePanel>

      {/* Plan & Tariff Details Panel */}
      <ExpandablePanel title="Plan Information">
        <div className="pb-6">
          <div className="space-y-6">
            {/* Plan Details Card */}
            <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {planData?.planName}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {planData?.shortName}
                  </p>
                </div>
                <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-200 shadow-none">
                  {planData?.statusDisplay}
                </Badge>
              </div>
              {/* HERE */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <div className="text-xs text-gray-500 mb-1">
                    Effective From
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {planData?.createdDate}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Effective To</div>
                  <div className="text-sm font-medium text-gray-900">
                    {planData?.validity}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Category</div>
                  <div className="text-sm font-medium text-gray-900 flex">
                    {planData?.category ? (
                      <Badge>{planData.category}</Badge>
                    ) : (
                      "NA"
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Sub Category</div>
                  <div className="text-sm font-medium text-gray-900 flex">
                    {planData?.subcategory ? (
                      <Badge>{planData.subcategory}</Badge>
                    ) : (
                      "NA"
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Services</div>
                  <div className="text-sm font-medium text-gray-900">
                    {planData?.utilityService?.join(", ")}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">
                    Billing Frequency
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {planData?.billingFrequencyDisplay}
                  </div>
                </div>
                {/* <div>
                  <div className="text-xs text-gray-500 mb-1">Tax Rate</div>
                  <div className="text-sm font-medium text-gray-900">
                    {planData?.tax}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Deposit</div>
                  <div className="text-sm font-medium text-gray-900">
                    {CURRENCY} {planData?.depositAmount}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">
                    Installation Charges
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {CURRENCY} {planData?.installationCharge}
                  </div>
                </div> */}
              </div>
            </div>

            {/* Utility Service Rates */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-4">
                Utility Service Rates
              </h4>
              <div className=" pt-0">
                {planData?.utilityService?.map((service, index) => (
                  <div key={index} className="border rounded-lg mb-4">
                    <button
                      onClick={() => toggleService(service)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900">
                        {service}
                      </span>
                      {expandedServices[service] ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedServices[service] && (
                      <div className="border-t bg-gray-50">
                        <div className="p-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">
                            Rate Information
                          </h4>
                          {planData?.utilityServiceData?.rate?.[service] && (
                            <div className="bg-white rounded-lg p-4 mb-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500">
                                    Rate Name
                                  </p>
                                  <p className="text-sm font-medium">
                                    {
                                      planData?.utilityServiceData?.rate[
                                        service
                                      ]?.rate?.rateName
                                    }
                                  </p>
                                </div>

                                <div>
                                  <label className="text-xs text-gray-500">
                                    Rate Type
                                  </label>
                                  <p className="text-sm font-medium">
                                    {planData?.utilityServiceData?.rate[service]
                                      ?.type === 0
                                      ? "Fixed Rate"
                                      : "Variable Rate"}
                                  </p>
                                </div>

                                <div>
                                  <label className="text-xs text-gray-500">
                                    Rate Validity
                                  </label>
                                  <p className="text-sm font-medium">
                                    {
                                      planData?.utilityServiceData?.rate?.[
                                        service
                                      ]?.rate?.effectiveDate
                                    }{" "}
                                    -{" "}
                                    {
                                      planData?.utilityServiceData?.rate?.[
                                        service
                                      ]?.rate?.expiryDate
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          {renderContent(service)}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Service Charges Details */}
            {Object.keys(
              planData?.utilityServiceData?.utilityServiceCharges || {},
            ).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">
                  Service Charges Details
                </h4>
                <div className="bg-white rounded-md border shadow-sm">
                  <div className="p-4 pt-0">
                    {Object.entries(
                      planData?.utilityServiceData?.utilityServiceCharges || {},
                    ).map(([chargeName, chargeData]) => {
                      const isUtilityService =
                        planData?.utilityService?.includes(chargeName);
                      const displayValue = isUtilityService
                        ? chargeData.consumption === "full"
                          ? "100%"
                          : `${chargeData.consumptionPercentage || "0"}%`
                        : `${CURRENCY} ${chargeData.fee}`;

                      return (
                        <div
                          key={chargeName}
                          className="flex justify-between items-center py-3 border-b last:border-b-0"
                        >
                          <div>
                            <span className="font-medium text-gray-900">
                              {formatLabel(chargeName)}
                            </span>
                            {isUtilityService && chargeData.consumption && (
                              <p className="text-xs text-gray-500">
                                Consumption: {chargeData.consumption}
                              </p>
                            )}
                          </div>
                          <span className="font-semibold text-gray-900">
                            {displayValue}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </ExpandablePanel>

      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={isSubmitting || isValidating}
        >
          Previous
        </Button>
        <div>
          <Button
            onClick={handleDraft}
            disabled={isSubmitting || isValidating}
            className="min-w-[200px] mr-5"
          >
            Save As Draft
          </Button>
          <Button
            onClick={handleContinue}
            disabled={isSubmitting || isValidating}
          >
            Continue to Readings & Consumption
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Step2AccountMetersPlans;
